
import React, { useState } from 'react';
import AppLayout from '@/components/app/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Search, Filter, Star } from 'lucide-react';

const AppShop = () => {
  const [activeTab, setActiveTab] = useState('Featured');
  const [showFilters, setShowFilters] = useState(false);

  const tabs = ['Featured', 'Memberships', 'Treatments', 'Packages', 'By Concern'];

  const featuredItems = [
    {
      type: 'Treatment',
      title: 'Teeth Whitening',
      description: 'Say hello to a brighter smile with same-day laser results.',
      image: '🦷',
      tags: ['Smile', 'Whitening', 'Treatment'],
      originalPrice: '$650',
      memberPrice: '$552.50',
      credits: 6,
      badge: '$25 Off',
      href: '/app/treatments/teeth-whitening'
    },
    {
      type: 'Package',
      title: 'Beauty Bank',
      description: 'Bank $99/month as JUV Credits. Redeem for any treatment.',
      image: '💳',
      tags: ['Bank', 'Package', 'Membership'],
      breakdown: ['$12/unit Botox', '$5/unit Dysport'],
      monthlyPrice: '$99',
      href: '/app/shop/beauty-bank'
    },
    {
      type: 'Treatment',
      title: 'AquaFirme Facial',
      description: 'Exfoliate, hydrate, and glow in one luxurious 30-minute treatment.',
      image: '💧',
      tags: ['Skin', 'Facial', 'Treatment'],
      originalPrice: '$300',
      memberPrice: '$255',
      credits: 4,
      badge: 'Best Seller',
      href: '/app/treatments/aquafirme'
    },
    {
      type: 'Treatment',
      title: 'TMJ Botox',
      description: 'Alleviate jaw tension and improve your smile balance.',
      image: '💉',
      tags: ['TMJ', 'Wellness', 'Treatment'],
      originalPrice: '$800',
      memberPrice: '$680',
      credits: 12,
      href: '/app/treatments/tmj-botox'
    }
  ];

  return (
    <AppLayout>
      <div className="px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-juv-deep-chestnut">Shop</h1>
          <div className="flex gap-2">
            <Button variant="outline" size="icon" className="border-juv-rose-clay/30 text-juv-deep-chestnut hover:bg-juv-soft-blush/30">
              <Search className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline" 
              size="icon"
              className="border-juv-rose-clay/30 text-juv-deep-chestnut hover:bg-juv-soft-blush/30"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? "default" : "outline"}
              size="sm"
              className={`whitespace-nowrap ${
                activeTab === tab 
                  ? 'bg-juv-terracotta hover:bg-juv-deep-chestnut text-white' 
                  : 'border-juv-rose-clay/30 text-juv-deep-chestnut hover:bg-juv-soft-blush/30'
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </Button>
          ))}
        </div>

        {/* Filters (Collapsible) */}
        {showFilters && (
          <Card className="mb-6 bg-white border-juv-rose-clay/20">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-juv-deep-chestnut mb-2">Service Type</h4>
                  <div className="space-y-1">
                    {['Treatment', 'Package', 'Membership'].map((type) => (
                      <label key={type} className="flex items-center gap-2 text-sm text-juv-deep-chestnut">
                        <input type="checkbox" className="rounded border-juv-rose-clay/30" />
                        {type}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-juv-deep-chestnut mb-2">Concern</h4>
                  <div className="space-y-1">
                    {['Acne', 'Anti-Aging', 'TMJ'].map((concern) => (
                      <label key={concern} className="flex items-center gap-2 text-sm text-juv-deep-chestnut">
                        <input type="checkbox" className="rounded border-juv-rose-clay/30" />
                        {concern}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Featured Items Grid */}
        <div className="space-y-4">
          {featuredItems.map((item, index) => (
            <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow bg-white border-juv-rose-clay/20">
              <CardContent className="p-0">
                <div className="flex">
                  {/* Image/Icon */}
                  <div className="w-20 h-20 bg-juv-soft-blush/30 flex items-center justify-center text-2xl border-r border-juv-rose-clay/10">
                    {item.image}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 p-4">
                    {/* Badge */}
                    {item.badge && (
                      <span className="inline-block px-2 py-1 bg-juv-terracotta text-white text-xs rounded-full mb-2">
                        {item.badge}
                      </span>
                    )}
                    
                    {/* Title & Description */}
                    <h3 className="font-semibold text-juv-deep-chestnut mb-1">{item.title}</h3>
                    <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                    
                    {/* Tags */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {item.tags.map((tag, tagIndex) => (
                        <span key={tagIndex} className="text-xs bg-juv-soft-blush/30 text-juv-deep-chestnut px-2 py-1 rounded border border-juv-rose-clay/20">
                          {tag}
                        </span>
                      ))}
                    </div>
                    
                    {/* Pricing */}
                    <div className="flex items-center justify-between">
                      <div>
                        {item.credits && (
                          <p className="text-juv-terracotta font-semibold">
                            {item.credits} JUV Credits
                          </p>
                        )}
                        {item.monthlyPrice && (
                          <p className="text-juv-terracotta font-semibold">
                            {item.monthlyPrice}/month
                          </p>
                        )}
                        {item.memberPrice && (
                          <p className="text-xs text-gray-500">
                            <span className="line-through">{item.originalPrice}</span> → {item.memberPrice} Member
                          </p>
                        )}
                        {item.breakdown && (
                          <div className="text-xs text-gray-600">
                            {item.breakdown.map((line, i) => (
                              <p key={i}>{line}</p>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button asChild size="sm" variant="outline" className="border-juv-rose-clay/30 text-juv-deep-chestnut hover:bg-juv-soft-blush/30">
                          <Link to={item.href}>Learn More</Link>
                        </Button>
                        {item.credits ? (
                          <Button asChild size="sm" className="bg-juv-terracotta hover:bg-juv-deep-chestnut">
                            <Link to="/app/book">Use Credits</Link>
                          </Button>
                        ) : (
                          <Button asChild size="sm" className="bg-juv-terracotta hover:bg-juv-deep-chestnut">
                            <Link to={item.href}>Join</Link>
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Browse by Concern CTA */}
        <Card className="mt-6 bg-gradient-to-r from-juv-soft-blush/50 to-juv-rose-clay/30 border-juv-rose-clay/20">
          <CardContent className="p-6 text-center">
            <h3 className="text-lg font-semibold text-juv-deep-chestnut mb-2">
              Browse by Your Concern
            </h3>
            <p className="text-gray-600 mb-4">
              Find the perfect treatment for your specific needs
            </p>
            <Button asChild className="bg-juv-terracotta hover:bg-juv-deep-chestnut">
              <Link to="/app/shop/concerns">Explore Concerns</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default AppShop;
